import {Location, OrderForm, Timetable, TimetableDate} from '~/types/types';
import {Ref} from 'vue';
import {useLayoutStore} from '~/stores/personal/layout';
import {useBackendStore} from '~/stores/backend'
import {getTimeTable as getTimeTableApi} from '~/api/order';
import {formatDate} from '~/common/date';
import {RawTimeTableV3} from '~/types/typesV3';
import {getSplitIndex, getTimetablePayload} from '~/common/objects';
import { storeToRefs } from 'pinia';
import {deepCopyObject, objTime} from '~/common/functions';
import { useDayjs } from '#dayjs'

const dayjs = useDayjs()

/**
 * Использовать только в orderStore.
 * Файл предназначен для разделения логики заказа и расписания, несмотря на то что они очень связаны друг с другом
 */
const useOrderTimeTable = (orderForm: Ref<OrderForm>, dispatchLocation: Ref<Location>, destinationLocation: Ref<Location>) => {
  const { setPageloader } = useLayoutStore()
  const { backendAvailable } = storeToRefs(useBackendStore())
  const { $showToast } = useNuxtApp()

  const timetable = ref(null as Timetable | null)
  const timetableData = ref<RawTimeTableV3>()
  /** почти deprecated use splitIndex. Нужен только для метода updateTimeTable и updateSplit */
  const destinationGroupDatesIndex = ref<number>()
  // Если флаг выставлен. То при следующем изменении timetable время будет оставаться таким же, если возможно.
  // Иначе сбрасывается на время по-умолчанию
  const skipDefaultTimeFlag = ref(false)
  let timetableController = new AbortController()

  const dispatchDate = computed((): TimetableDate | null => {
    return timetable.value?.dispatch?.find(d => d.isDefault) as TimetableDate
  })

  const destinationDate = computed((): TimetableDate | null => {
    return timetable.value?.destination?.[splitIndex.value || 0]?.dates?.find(d => d.isDefault) as TimetableDate
  })

  const hasFakeLocation = computed(() => {
    return Number(dispatchLocation.value?.fake) === 1 || Number(destinationLocation.value?.fake) === 1
  })

  const splitTime = computed(() => {
    const { calendar } = orderForm.value.directions.dispatch
    const isAddress = orderForm.value.directions.dispatch.direction === 'address'
    return dayjs.duration(objTime(isAddress ? calendar.to : calendar.terminalTime)).asMinutes()
  })

  const splitIndex = computed(() => {
    return getSplitIndex(timetable.value, splitTime.value)
  })

  const timetablePayload = computed(() => {
    return getTimetablePayload(orderForm.value)
  })

  function updateSplit(tt: TimetableDate|Timetable|null = null) {
    if (orderForm.value?.meta?.lockState?.destination) {
      return
    }
    if (tt === null) {
      tt = timetable.value
    }
    const { calendar } = orderForm.value.directions.dispatch
    const dispatchToTime = dayjs.duration(objTime(orderForm.value.directions.dispatch.direction === 'address' ? calendar.to : calendar.terminalTime)).asMinutes()
    const oldSplitIndex = destinationGroupDatesIndex.value
    let newSplitIndex = oldSplitIndex

    if ((tt as Timetable)?.destination?.length > 1) {
      const times = (tt as Timetable)?.destination.map(g => g.groupTimeMin)
      if (dispatchToTime <= times[0]) {
        newSplitIndex = 0
      } else if (times[2] && (dispatchToTime >= times[2])) {
        newSplitIndex = 2
      } else {
        newSplitIndex = 1
      }
    } else {
      newSplitIndex = 0
    }
    if (oldSplitIndex !== newSplitIndex) {
      destinationGroupDatesIndex.value = newSplitIndex
    }
  }

  function setDefaultTime(timetable: RawTimeTableV3, alert = false) {
    // alias
    const calendar = orderForm.value.directions.dispatch.calendar
    const date = deepCopyObject(timetable.dispatch?.find(d => d.isDefault))
    const optimal = date?.optimalTime || { from: '', to: '' }
    let alertText = 'Время отправки изменилось на время по умолчанию'
    optimal.from = optimal.from ? formatDate(optimal.from, 'HH:mm') : ''
    optimal.to = optimal.to ? formatDate(optimal.to, 'HH:mm') : ''

    if (skipDefaultTimeFlag.value) {
      skipDefaultTimeFlag.value = false
      return
    }

    if (orderForm.value.directions.dispatch.direction === 'address') {
      if (optimal.from === calendar.from && optimal.to === calendar.to) {
        // Время не изменится
        return
      }
      if (calendar.fixedTime) {
        // В это случае сообщение и выставление времени будет в компоненте vz-direction-calendar-timer
        return;
      }
      if (!calendar.from || !calendar.to) {
        alert = false
      }
    } else {
      alert = false
    }

    calendar.from = optimal.from
    calendar.to = optimal.to
    calendar.terminalTime = ''
    destinationGroupDatesIndex.value = 0
    if (alert) {
      $showToast({ variant: 'info', title: 'Внимание', text: alertText })
    }
  }

  // Обновляем timetableData, когда изменяется payload. flush: 'post' чтобы дождаться всех изменений
  watch(
      () => timetablePayload.value,
      async (value, oldValue) => {
        if (!value.dispatch.locationGuid || !value.destination.locationGuid) {
          return
        }

        if (JSON.stringify(value) === JSON.stringify(oldValue) || hasFakeLocation.value) {
          return
        }

        // TODO: если город отправки и прибытия одинаковый и в обоих терминал то выходим
        // если это правило включить, то не будет отрабатывать сообщение от 1с в getTimeTableApi
        // if (value.dispatch.terminalGuid && value.dispatch.terminalGuid === value.destination.terminalGuid) {
        //   return
        // }

        oldValue.dispatch.date = dispatchDate.value?.date || ''
        if ((!timetablePayload.value.dispatch.locationGuid || JSON.stringify(value) === JSON.stringify(oldValue)) &&
            orderForm.value.directions.destination.calendar.date) {
          // Если нет города отправки, или запрос отличается только датой, а текущая дата пришла из ответа, то не делаем новый запрос.
          // Такое бывает если отдавали пустую дату или выбранная дата недоступна, тогда выставляем дефолт дату.
          // upd при клонировании сбрасывалась дата прибытия через setDefaultDate
          return
        }

        timetableController.abort()
        timetableController = new AbortController()
        setPageloader(true)
        const timetable = await getTimeTableApi(timetablePayload.value, timetableController.signal, !backendAvailable.value)
            .finally(() => {
              setPageloader(false)
            })
        const dispatchChange = !!oldValue.dispatch.locationGuid && value.dispatch.locationGuid !== oldValue.dispatch.locationGuid
        const destinationChange = !!oldValue.destination.locationGuid && value.destination.locationGuid !== oldValue.destination.locationGuid

        if (!timetable) {
          return
        }
        if (timetable.error?.message) {
          if (backendAvailable.value) {
            $showToast({
              variant: 'error',
              title: 'Ошибка',
              text: timetable.error?.message,
              iconName: 'alert',
              autocloseDelay: 6000
            })
          }
          return
        }
        if (oldValue.orderGuid === value.orderGuid && ((oldValue.dispatch.date && value.dispatch.date !== oldValue.dispatch.date) || dispatchChange || destinationChange)) {
          // При смене даты/города - время и split ставить по дефолту
          setDefaultTime(timetable, destinationChange)
        }
        timetableData.value = timetable
      },
      { flush: 'post' }
  )

  return {
    timetable,
    timetableData,
    destinationGroupDatesIndex,
    dispatchDate,
    destinationDate,
    timetablePayload,
    splitTime,
    splitIndex,
    skipDefaultTimeFlag,

    updateSplit,
  }
}

export default useOrderTimeTable
